/**
 * Light or dark, remembered.
 *
 * Defaults to the operating system's preference rather than to light, and only
 * pins a choice once one has actually been made - so a machine set to dark
 * does not flash white on every visit.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { getTheme } from '../theme'
import type { ThemeMode } from '../theme'

const STORAGE_KEY = 'best-theme-mode'

interface ThemeModeApi {
  mode: ThemeMode
  toggle: () => void
}

const ThemeModeContext = createContext<ThemeModeApi | null>(null)

function stored(): ThemeMode | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return value === 'light' || value === 'dark' ? value : null
  } catch {
    // Private browsing and locked-down profiles can refuse localStorage, and a
    // theme preference is never worth failing to render over.
    return null
  }
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)')
  const [chosen, setChosen] = useState<ThemeMode | null>(() => stored())

  const mode: ThemeMode = chosen ?? (prefersDark ? 'dark' : 'light')

  useEffect(() => {
    if (!chosen) return
    try {
      localStorage.setItem(STORAGE_KEY, chosen)
    } catch {
      // See above; remembering is a convenience, not a requirement.
    }
  }, [chosen])

  const value = useMemo<ThemeModeApi>(
    () => ({ mode, toggle: () => setChosen(mode === 'dark' ? 'light' : 'dark') }),
    [mode],
  )

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={getTheme(mode)}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  )
}

export function useThemeMode(): ThemeModeApi {
  const value = useContext(ThemeModeContext)
  if (!value) throw new Error('useThemeMode must be used inside a ThemeModeProvider')
  return value
}
